/**
 * FlyoutDrawer — shared inline right-side drawer used by all V2 CRUD pages.
 *
 * Renders as a flex sibling (not a position:fixed overlay) so that sibling
 * panels like Archie reflow the page layout and nothing overlaps.
 *
 * Provides: header (title + close button), scrollable content area, footer area.
 */

import { Box } from "@mui/material";
import {
  closeButtonSx,
  drawerHeaderSx,
  drawerTitleSx,
  drawerScrollAreaSx,
  inlineDrawerSx,
} from "./crudStyles";

function CloseButton({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Box component="button" onClick={onClick} sx={closeButtonSx}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16 }}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </Box>
  );
}

interface FlyoutDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: React.ReactNode;
  readonly footer?: React.ReactNode;
}

export default function FlyoutDrawer({
  open,
  onClose,
  title,
  children,
  footer,
}: FlyoutDrawerProps) {
  if (!open) return null;

  return (
    <Box sx={inlineDrawerSx("flyoutSlideIn")}>
      <Box sx={drawerHeaderSx}>
        <Box sx={drawerTitleSx}>{title}</Box>
        <CloseButton onClick={onClose} />
      </Box>

      <Box sx={drawerScrollAreaSx}>{children}</Box>

      {footer}
    </Box>
  );
}
